/**
 * Curated founder-facing explanations for Bridge45 domain terms.
 * Only non-common concepts belong here — wire via TermHelp, not a glossary page.
 * Keep copy aligned with derive.ts (runway math) and constants.ts (evidence ladder).
 */

export type TermKey = keyof typeof TERMINOLOGY;

export type TermDef = {
  /** Short title shown in the tooltip header when useful. */
  title?: string;
  /** One or two plain-language sentences. */
  body: string;
};

export const TERMINOLOGY = {
  // ---- Runway inputs -------------------------------------------------------
  startingCash: {
    title: 'Starting cash',
    body: 'Cash recorded at Sprint Initialization (T0). Used as a baseline reference — current runway is calculated from cash on hand, not this figure.',
  },
  cashOnHand: {
    title: 'Cash on hand',
    body: 'What you can spend today. Current runway = cash on hand ÷ current monthly burn. Leave blank if unknown — Unknown stays Unknown.',
  },
  expectedProofSpend: {
    title: 'Expected proof spend',
    body: 'Cash you intentionally plan to spend this sprint to buy commercial or regulatory evidence (PROVE). A budget for proof, not the derived total of recorded PROVE expenses.',
  },
  currentMonthlyBurn: {
    title: 'Current monthly burn',
    body: 'Net cash leaving the company each month at today’s spending rate. This is the burn used for current runway.',
  },
  minViableBurn: {
    title: 'Minimum viable burn',
    body: 'The lowest monthly spend you could sustain while still operating. Used for minimum-burn runway — how long cash lasts if you cut to survival mode.',
  },
  burnAtSprintStart: {
    title: 'Burn at sprint start',
    body: 'Monthly burn on Day 1. Compared with current burn to measure cash saved during the sprint and whether burn has decreased.',
  },
  currentRunway: {
    title: 'Current runway',
    body: 'Months of cash left at the current monthly burn (cash on hand ÷ current burn). Confirmed-zero burn means unbounded runway at current spend.',
  },
  minBurnRunway: {
    title: 'Minimum-burn runway',
    body: 'Months of cash left if you cut to minimum viable burn. Shows how much time a burn cut would buy.',
  },
  cashSavedSprint: {
    title: 'Cash saved during sprint',
    body: 'Estimated cash preserved by lowering burn since Day 1: (burn at sprint start − current burn) × months elapsed.',
  },

  // ---- Expense categories --------------------------------------------------
  expenseKeep: {
    title: 'KEEP',
    body: 'Spend required for company or platform survival. Not optional in the short term.',
  },
  expenseProve: {
    title: 'PROVE',
    body: 'Spend that creates commercial or regulatory evidence — the kind of cash that should buy E4/E5 movement.',
  },
  expenseExtend: {
    title: 'EXTEND',
    body: 'Spend that extends runway or financing (fundraising costs, bridge capital work). Buys time, not product features.',
  },
  expenseStop: {
    title: 'STOP',
    body: 'Everything else. Classification is not a decision — STOP spend still needs an explicit stop or justification.',
  },

  // ---- Settings / sprint ---------------------------------------------------
  sprintT0: {
    title: 'Sprint Initialization (T0)',
    body: 'Recorded Day 1 of the 45-day sprint. Day 15, 30, and 45 gate dates are derived from this date. Changing T0 after start requires an attributed Re-baseline Decision.',
  },
  rebaseline: {
    title: 'Re-baseline sprint',
    body: 'Moves Sprint Initialization (T0) and regenerates gate dates. Requires a written rationale and writes a Decision Log entry — it is not a casual date edit.',
  },
  idleProposalDays: {
    title: 'Idle proposal',
    body: 'Business days a priced proposal can sit without movement before Attention Needed flags it.',
  },
  idleTierADays: {
    title: 'Idle Tier A',
    body: 'Business days a Tier A (priority) account can sit without a next action before it is flagged.',
  },
  runwayWatchMonths: {
    title: 'Runway watch',
    body: 'Below this many months of current runway, status becomes Watch (amber).',
  },
  runwayCriticalMonths: {
    title: 'Runway critical',
    body: 'Below this many months of current runway, status becomes Critical (red).',
  },
  materialSpend: {
    title: 'Material spend',
    body: 'Expense amount at or above which an evidence objective is required. Material spend without an objective is unjustified.',
  },

  // ---- Funnel targets ------------------------------------------------------
  marketUniverse: {
    title: 'Market universe',
    body: 'All researched accounts in the Dubai funnel — the raw pool. You do not work the whole universe; you screen into qualified targets and Tier A.',
  },
  qualifiedTargets: {
    title: 'Qualified targets',
    body: 'Accounts that passed screening (Qualified Target, Tier A, or Tier B). Fit enough to consider, not yet a deal.',
  },
  activeTierA: {
    title: 'Active Tier A',
    body: 'Priority accounts that earned founder attention. Cap is intentional — Tier A is a designation, not a vanity queue.',
  },
  substantiveConversations: {
    title: 'Substantive conversations',
    body: 'Commercial discovery conversations (not cold outreach). The counterparty engaged on a real problem or use case.',
  },
  qualifiedOpportunities: {
    title: 'Qualified opportunities',
    body: 'Live opportunities that meet qualification — a real path, not an Observed lead. Density before attachment.',
  },
  credibleAssets: {
    title: 'Credible assets',
    body: 'Named assets that have surfaced for screening. D001 needs a suitable asset, not only an interested sponsor.',
  },
  d001Candidates: {
    title: 'D001 candidates',
    body: 'Qualified opportunities marked as possible first Dubai transactions. Contenders for Primary D001.',
  },
  primaryD001: {
    title: 'Primary D001',
    body: 'The single designated first Dubai transaction. One primary — backups exist so the strategy survives if it dies.',
  },
  e4E5Commitments: {
    title: 'E4 / E5 commitments',
    body: 'Target count of commercial commitments (E4) and binding proof (E5) in Dubai. E4 = budget / proposal / mandate path; E5 = paid, signed, or executable commitment. Not the same as founder Today promises or an asset commitment.',
  },
  founderCommitment: {
    title: 'Founder commitment',
    body: 'A move the founder pinned to Today — a promise to execute. Closing it records the outcome; it is not commercial evidence by itself. Preparation-only closes create neither proof nor a founder commitment.',
  },
  assetCommitment: {
    title: 'Asset commitment',
    body: 'The counterparty names a concrete asset and shares enough information to screen it (typically E3). Without a credible asset, a sponsor conversation cannot become D001.',
  },

  // ---- Pipeline aging ------------------------------------------------------
  agingTierA: {
    title: 'Tier A contacted, no movement',
    body: 'Business days after contact with no stage movement before a Tier A account is called stale.',
  },
  agingDiscovery: {
    title: 'Discovery done, no next action',
    body: 'Business days after discovery without a next action before the opportunity is flagged.',
  },
  agingQualified: {
    title: 'Qualified opportunity static',
    body: 'Business days a qualified opportunity can sit without progress before it is stale.',
  },
  agingD001: {
    title: 'D001 candidate not progressing',
    body: 'Business days a D001 candidate can stall before Attention Needed treats it as stuck.',
  },

  // ---- Evidence ladder -----------------------------------------------------
  evidenceE0: {
    title: 'E0 — Hypothesis',
    body: 'We think they might care. No traction credit.',
  },
  evidenceE1: {
    title: 'E1 — Engagement',
    body: 'Reply, introduction, or meeting booked. Leading indicator only — not traction.',
  },
  evidenceE2: {
    title: 'E2 — Problem confirmed',
    body: 'Meeting occurred and the counterparty explicitly confirmed a relevant problem or use case. Real, but not yet commercial.',
  },
  evidenceE3: {
    title: 'E3 — Commitment of time / information',
    body: 'The counterparty commits information, time, or resources (named asset, documents, data room, second meeting). They are spending their own resources on you.',
  },
  evidenceE4: {
    title: 'E4 — Commercial / execution commitment',
    body: 'Budget, proposal requested, paid POC under approval, mandate negotiation, partner architecture accepted, or a formal regulatory next gate. Meaningful traction.',
  },
  evidenceE5: {
    title: 'E5 — Binding proof',
    body: 'Paid, signed, contract, mandate, or executable regulated partner commitment. The strongest proof.',
  },
  evidenceAct: {
    title: 'Activity',
    body: 'Motion — research, sending, preparing, building, posting. Necessary sometimes. Never proof.',
  },
  evidenceLadder: {
    title: 'Evidence ladder',
    body: 'Only counterparty actions climb the ladder. Internal work is Activity. Counts are by level — never a blended score.',
  },

  // ---- Autopilot / Bridge --------------------------------------------------
  bridgeAutopilot: {
    title: 'Autopilot',
    body: 'When enabled (and a provider is configured), The Bridge may re-study the plan in the background. Unchanged state does not spend tokens.',
  },
  restudy: {
    title: 'Re-study',
    body: 'Forces The Bridge to rebuild an internal plan version from current recorded state. Rejected versions fail policy validation and are not applied.',
  },
} as const satisfies Record<string, TermDef>;

export function termBody(key: string | undefined | null): string | null {
  if (!key) return null;
  const def = (TERMINOLOGY as Record<string, TermDef>)[key];
  return def?.body ?? null;
}

export function termDef(key: string | undefined | null): TermDef | null {
  if (!key) return null;
  return (TERMINOLOGY as Record<string, TermDef>)[key] ?? null;
}
